/**
 * Coupon save — plan §9.
 *
 * Two rules drive the shape of this file, and both are platform facts rather than taste.
 *
 * 1. **The `File` is built before the tap, never inside the handler.** `navigator.share()` requires
 *    transient user activation, and an `await` between the tap and the call consumes it — iOS then
 *    rejects with `NotAllowedError` even though the user really did tap. So preparation (fetch →
 *    Blob → File) is a separate async step run on entering RESULT, and `saveCoupon()` stays
 *    synchronous up to the `share()` call.
 *
 * 2. **Branch by platform, never an ordered fallback chain.** Android Chrome (75+) also supports
 *    Web Share with files, so "try share, else download" never reaches `<a download>` on the
 *    platform that wanted it — the coupon would open a share sheet instead of landing in Downloads.
 */

/** How the coupon reaches the device. Chosen once from the platform, never retried in order. */
export type SaveStrategy =
  /** `<a download>` — lands in Downloads, not the gallery. Android and desktop. */
  | 'download'
  /** Show the image full-screen and let the user long-press → "사진에 추가". The iOS default. */
  | 'longPress'
  /** The OS share sheet. iOS only, and only when `COUPON.iosMode` selects it. */
  | 'share';

export type SaveOutcome =
  /** The OS confirmed completion. Only `share()` can report this. */
  | { status: 'saved' }
  /** Download fired. The browser reports neither success nor failure — see `handedOff` below. */
  | { status: 'downloaded' }
  /**
   * Handed to the platform with no completion signal. `<a download>` and long-press both return
   * nothing, so the user has to tell us the save happened (plan §9).
   */
  | { status: 'handedOff' }
  /** Share sheet dismissed. Stay in RESULT silently and let the user tap again. */
  | { status: 'cancelled' }
  | { status: 'failed'; error: unknown };

export interface PreparedCoupon {
  readonly strategy: SaveStrategy;
  readonly file: File;
  /** Object URL for the long-press view and the download anchor. Free it with `releaseCoupon()`. */
  readonly objectUrl: string;
  readonly fileName: string;
}

/** iOS route. `layer` shows the image for a long-press save; `share` opens the OS share sheet. */
export type IosMode = 'layer' | 'share';

export interface PrepareOptions {
  readonly url: string;
  /** `{code}` is substituted with the draw's coupon code. */
  readonly fileNamePattern: string;
  readonly couponCode: string;
  readonly isIos: boolean;
  readonly iosMode: IosMode;
  readonly signal?: AbortSignal;
}

/**
 * Keep the download name and the share-sheet name from drifting apart by deriving both here.
 * The result reaches a file system, not just the DOM, so the character class stays conservative.
 */
function resolveFileName(pattern: string, code: string): string {
  const safeCode = code.replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'coupon';
  return pattern.replace('{code}', safeCode);
}

function pickStrategy(file: File, isIos: boolean, iosMode: IosMode): SaveStrategy {
  if (isIos) {
    // `canShare` with a files payload is the only honest capability test — `navigator.share` exists
    // on browsers that reject file payloads outright.
    const canShareFile =
      iosMode === 'share' &&
      typeof navigator.canShare === 'function' &&
      typeof navigator.share === 'function' &&
      navigator.canShare({ files: [file] });

    // `<a download>` is deliberately not a fallback here: on iOS Safari it opens the file instead of
    // saving it, so the long-press guide is the only route into Photos when sharing is off.
    return canShareFile ? 'share' : 'longPress';
  }

  return 'download' in document.createElement('a') ? 'download' : 'longPress';
}

/**
 * Fetch the coupon and wrap it as a `File`. Call on entering RESULT — never from a tap handler.
 *
 * Throws if the coupon cannot be fetched or is not an image. The content-type check is not
 * paranoia: under an SPA fallback a missing asset answers 200 with `index.html`, and without it the
 * user gets a file named `.png` full of HTML (doc/project_info.md, Known Issues 2026-07-30).
 */
export async function prepareCoupon(options: PrepareOptions): Promise<PreparedCoupon> {
  const { url, fileNamePattern, couponCode, isIos, iosMode, signal } = options;

  // `signal ?? null`, not `signal`: `exactOptionalPropertyTypes` refuses an explicit `undefined`
  // here, and RequestInit models "no signal" as null.
  const response = await fetch(url, { signal: signal ?? null, cache: 'no-store' });
  if (!response.ok) throw new Error(`coupon fetch failed: ${response.status} ${url}`);

  const type = response.headers.get('content-type') ?? '';
  if (!type.startsWith('image/')) {
    throw new Error(`coupon is not an image: content-type "${type}" at ${url}`);
  }

  const blob = await response.blob();
  const fileName = resolveFileName(fileNamePattern, couponCode);
  const file = new File([blob], fileName, { type: blob.type || type });

  return {
    strategy: pickStrategy(file, isIos, iosMode),
    file,
    objectUrl: URL.createObjectURL(blob),
    fileName,
  };
}

/**
 * Revocation is deferred, and that delay is load-bearing.
 *
 * `<a download>` starts an asynchronous read of the object URL, and the very next thing the app
 * does is transition RESULT → DONE, which tears the coupon down. Revoking in that same task can
 * cancel a download that had only just begun — an intermittent "nothing appeared in Downloads" that
 * would look like a platform quirk. A few idle seconds cost nothing and remove the race entirely.
 */
const REVOKE_DELAY_MS = 10_000;

export function releaseCoupon(prepared: PreparedCoupon | null): void {
  if (!prepared) return;
  const { objectUrl } = prepared;
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), REVOKE_DELAY_MS);
}

/**
 * Hand the prepared coupon to the platform.
 *
 * MUST be called directly from the tap handler. It returns a Promise, but `navigator.share()` runs
 * synchronously before the first suspension point, so the user activation is still intact when the
 * OS checks for it.
 *
 * `longPress` returns `handedOff` without doing anything — the caller renders the image layer.
 */
export function saveCoupon(prepared: PreparedCoupon): Promise<SaveOutcome> {
  if (prepared.strategy === 'share') {
    return navigator
      .share({ files: [prepared.file] })
      .then<SaveOutcome>(() => ({ status: 'saved' }))
      .catch<SaveOutcome>((error: unknown) =>
        // Dismissing the sheet is not a failure — stay put and let the user tap again.
        isAbort(error) ? { status: 'cancelled' } : { status: 'failed', error },
      );
  }

  if (prepared.strategy === 'download') {
    try {
      triggerDownload(prepared);
    } catch (error) {
      return Promise.resolve({ status: 'failed', error });
    }
    return Promise.resolve({ status: 'downloaded' });
  }

  return Promise.resolve({ status: 'handedOff' });
}

function triggerDownload(prepared: PreparedCoupon): void {
  const a = document.createElement('a');
  a.href = prepared.objectUrl;
  a.download = prepared.fileName;
  a.rel = 'noopener';
  // Some Android builds ignore a click on a node that was never in the document.
  a.style.display = 'none';
  document.body.append(a);
  a.click();
  a.remove();
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}
